/**
 * "Check printing" for a trade's customer lines (checkPrinting.ts), from the scanner's lookup: the
 * lines' cards are looked up together (≤50 per request, each card cached for a minute), so the till
 * asks once per new card, not once per refresh.
 */
import { useMemo } from 'react'
import { checkPrinting, type PrintingCheck } from './checkPrinting'
import { useScannerLookup } from './scannerLookup'

type Line = { id: string; cardId?: string | null; printing?: string | null; condition: string }

/** Flagged lines only, by line id. */
export function usePrintingChecks(lines: readonly Line[] | null | undefined): ReadonlyMap<string, PrintingCheck> {
  const cardIds = useMemo(
    () => (lines ?? []).map((l) => l.cardId).filter((id): id is string => typeof id === 'string' && id.length > 0),
    [lines],
  )
  const lookup = useScannerLookup(cardIds)
  const flagged = new Map<string, PrintingCheck>()
  for (const line of lines ?? []) {
    if (!line.cardId) continue
    const check = checkPrinting(lookup.byId.get(line.cardId), line.printing, line.condition)
    if (check?.flagged) flagged.set(line.id, check)
  }
  return flagged
}
